import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Mail, Copy, Loader2, Check } from "lucide-react";
import { fmtPhone } from "@/lib/format";

const ROLE_LABEL = { vendedor: "proprietário", comprador: "comprador" };
const btn = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40";

function Chips({ items, sel, toggle }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="remind-items">
      {items.map((i) => {
        const on = sel.includes(i.key);
        return (
          <button key={i.key} type="button" onClick={() => toggle(i.key)} data-testid={`remind-item-${i.key}`}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-ec-green text-white border-ec-green" : "bg-white text-slate-500 border-slate-200 line-through"}`}>
            {on && <Check size={13} />}{i.name}
            {i.reason && <span className={on ? "text-red-200" : ""}> · {i.reason}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function RemindDialog({ pid, role, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [text, setText] = useState("");
  const [to, setTo] = useState({ phone: "", email: "" });
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = (keys, tk) => api.get(`/processes/${pid}/reminder/${role}`, {
    params: { base_url: window.location.origin, token: tk || undefined, keys: keys ? keys.join(",") : undefined },
  }).then(({ data }) => { setData(data); setText(data.text || ""); return data; });
  useEffect(() => {
    if (!role) { return; }
    load(null, null).then((d) => { setSel(d.items.map((i) => i.key)); setToken(d.token); setTo({ phone: fmtPhone(d.to.phone) || "", email: d.to.email || "" }); })
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail)); onClose(); });
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) => { const s = sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]; setSel(s); if (s.length) load(s, token); };
  const pick = (tk) => { setToken(tk); load(sel, tk).then((d) => setTo({ phone: fmtPhone(d.to.phone) || "", email: d.to.email || "" })); };
  const send = async (channel) => {
    const win = channel === "whatsapp" ? window.open("", "_blank") : null;
    setBusy(channel);
    try {
      const { data: r } = await api.post(`/processes/${pid}/reminder/${role}/send`,
        { channel, token, keys: sel, text, phone: to.phone, email: to.email, base_url: window.location.origin });
      if (channel === "whatsapp" && win) win.location.href = r.wa_url;
      if (channel === "copy") await navigator.clipboard.writeText(text).catch(() => {});
      toast.success({ whatsapp: "WhatsApp aberto · envio registado", email: "Email enviado", copy: "Mensagem copiada · envio registado" }[channel]);
      onSent(); onClose();
    } catch (e) { win?.close(); toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  if (!role) return null;
  const noContact = data && !to.phone && !to.email;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="remind-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Lembrar {ROLE_LABEL[role]}</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : !data.items.length ? (
          <p className="text-[15px] text-slate-600 py-2" data-testid="remind-nothing">Já não falta nada a {data.to.name || ROLE_LABEL[role]}. Não há nada para lembrar.</p>
        ) : (
          <div className="space-y-4">
            <div data-testid="remind-to">
              <Label className="text-xs text-slate-500">Para</Label>
              <div className="text-[15px] text-ec-green font-medium">{data.to.name}{data.to.company && <span className="text-slate-500 font-normal"> · gerente de {data.to.company}</span>}</div>
              {data.people.length > 1 && (
                <div className="flex gap-2 mt-1.5">{data.people.map((p) => (
                  <button key={p.token} onClick={() => pick(p.token)} data-testid={`remind-person-${p.token}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${p.token === token ? "border-ec-green text-ec-green" : "border-slate-200 text-slate-500"}`}>{p.name}</button>
                ))}</div>
              )}
              {noContact && <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-2.5 py-1.5 mt-2" data-testid="remind-no-contact">Sem contacto. Indique o telemóvel ou o email; fica guardado na parte e na página do cliente.</div>}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Input value={to.phone} onChange={(e) => setTo({ ...to, phone: e.target.value })} placeholder="Telemóvel" inputMode="tel" data-testid="remind-phone" />
                <Input value={to.email} onChange={(e) => setTo({ ...to, email: e.target.value })} placeholder="Email" type="email" data-testid="remind-email" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Incluir</Label>
              <div className="mt-1.5"><Chips items={data.items} sel={sel} toggle={toggle} /></div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Mensagem</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={11} className="mt-1.5 text-sm" data-testid="remind-text" />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => send("copy")} disabled={!!busy || !sel.length} className={`${btn} border border-slate-200 text-ec-green hover:bg-slate-50`} data-testid="remind-copy">
                {busy === "copy" ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Copiar</button>
              <button onClick={() => send("email")} disabled={!!busy || !sel.length || !to.email} className={`${btn} border border-slate-200 text-ec-green hover:bg-slate-50`} data-testid="remind-email-send">
                {busy === "email" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email</button>
              <button onClick={() => send("whatsapp")} disabled={!!busy || !sel.length || to.phone.replace(/\D/g, "").length < 9} className={`${btn} bg-ec-green text-white hover:bg-ec-greenDark`} data-testid="remind-whatsapp">
                {busy === "whatsapp" ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} WhatsApp</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
